import type { ApplicationContext } from '@hc-frontend/core-context';
import {
  CountMetric,
  CountMetricName,
  type JourneyAlias,
  Placement,
  type ProductType,
} from '@hc-frontend/core-types';
import type { IOperationError } from '@hc-frontend/core-utils-business-operation';
import { createErrorOperationResult } from '@hc-frontend/core-utils-business-operation';

import { PlateName } from '../../entities';
import type {
  JourneyRepository,
  MoveToStartPlateRepository,
  MoveToStartPlateResult,
} from '../../repositories';

export type MoveToStartPlateUseCase = (
  journeyAlias: JourneyAlias,
  product: ProductType,
) => Promise<MoveToStartPlateResult>;

export type MoveToStartPlateFactoryParams = Pick<
  ApplicationContext,
  'log' | 'trackCountMetric'
> &
  Pick<JourneyRepository, 'getJourney'> &
  MoveToStartPlateRepository;

async function logOperation<T extends string>(
  log: ApplicationContext['log'],
  result: IOperationError<T>,
) {
  log({
    context: 'server',
    message: result.code,
    type: 'error',
    data: {
      rootCause: result.rootCause,
    },
  });
}

export function moveToStartPlateFactory({
  moveToStartPlate,
  getJourney,
  log,
  trackCountMetric,
}: MoveToStartPlateFactoryParams): MoveToStartPlateUseCase {
  return async (journeyAlias, product) => {
    const getJourneyResult = await getJourney(journeyAlias);

    if (!getJourneyResult.success) {
      logOperation(log, getJourneyResult.errors[0]);

      return getJourneyResult;
    }

    const journey = getJourneyResult.data;

    const startingPlate = journey.plates[journey.start];

    if (!startingPlate) {
      const result = createErrorOperationResult({
        errors: [
          {
            code: 'failedToGetJourney',
            rootCause: new Error(
              'The start plate is not configured properly on the journey',
            ),
          },
        ],
      });

      logOperation(log, result.errors[0]);
      return result;
    }

    const event: CountMetric = {
      name: CountMetricName.Enum['page-view'],
      tags: {
        platform: 'apollo',
        journey_id: journey.id.toString(),
        plate_name: PlateName.Enum.INITIAL,
        plate_index: -1,
        product,
        placement: Placement.Enum.SURVEY,
        journey_end: false,
      },
    };

    trackCountMetric(event);

    return moveToStartPlate({ journeyAlias, plate: startingPlate });
  };
}
